import { zodResolver } from '@hookform/resolvers/zod'
import { loginSchema } from '@repo/shared'
import { Button, Input } from '@repo/ui'
import { Link, createFileRoute } from '@tanstack/react-router'
import { Eye, EyeOff, LogIn } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import useLogin from '../../lib/api/auth/login'

import type { LoginSchema } from '@repo/shared'
import type React from 'react'

import Logo from '@/assets/logo.svg?react'
import LanguageSettings from '@/components/layout/header/language-settings'
import FormControl from '@/components/shared/form-control'
import { useDocumentTitle } from '@/hooks/use-document-title'
import { localizedPageTitle } from '@/lib/page-title'

export const Route = createFileRoute('/(auth)/login')({
  head: () => ({ meta: [{ title: localizedPageTitle('login') }] }),
  component: LoginPage,
})

function LoginPage() {
  useDocumentTitle('login')
  const { t } = useTranslation('login')

  const { mutate: login, isPending, errorMessage } = useLogin()
  const [showPassword, setShowPassword] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginSchema>({
    resolver: zodResolver(loginSchema),
  })

  const onSubmit = (data: LoginSchema) => {
    login(data)
  }

  return (
    <div className="flex min-h-dvh">
      {/* Left Panel - Login Form */}
      <div className="bg-card flex min-h-dvh w-full flex-col justify-between border-r md:max-w-[500px]">
        {/* Header */}
        <div className="border-border flex items-center gap-3 border-b md:gap-6">
          <div className="border-r p-4 md:p-6">
            <h1 className="text-base font-semibold md:text-lg">
              {t('loginTitle')}
            </h1>
          </div>

          <Logo className="h-6 shrink-0 md:h-auto" />

          <div className="ml-auto [&>div]:border-r-0">
            <LanguageSettings />
          </div>
        </div>

        {/* Form */}
        <div className="mx-auto mt-6 w-full px-4 md:px-6">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <FormControl
              htmlFor="email"
              labelText={t('usernameOrEmail')}
              error={errors.email?.message}
            >
              <Input
                id="email"
                {...register('email')}
                type="email"
                placeholder="admin@fleet.com"
                className="bg-background"
              />
            </FormControl>

            <FormControl
              htmlFor="password"
              labelText={t('password')}
              error={errors.password?.message}
            >
              <div className="relative">
                <Input
                  id="password"
                  {...register('password')}
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  className="bg-background pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="text-muted-foreground hover:text-foreground absolute inset-y-0 right-0 flex items-center px-3"
                  aria-label={
                    showPassword ? t('hidePassword') : t('showPassword')
                  }
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <EyeOff className="size-4" />
                  ) : (
                    <Eye className="size-4" />
                  )}
                </button>
              </div>
            </FormControl>

            <div className="flex justify-end">
              <Link
                to="/forgot-password"
                className="text-primary text-sm hover:underline"
              >
                {t('forgotPassword')}
              </Link>
            </div>

            <Button
              disabled={isPending}
              isLoading={isPending}
              type="submit"
              variant="secondary"
              size="xl"
              className="border-border primary-bg-gradient dark:text-primary-foreground w-full border"
            >
              <LogIn className="size-4" />
              {t('loginTitle')}
            </Button>
          </form>
          {errorMessage && (
            <p className="text-destructive mt-4 text-center text-sm">
              {errorMessage}
            </p>
          )}
        </div>

        <p className="text-muted-foreground mt-auto px-4 pb-4 text-xs md:px-6 md:pb-6">
          {t('allRightsReserved')}
        </p>

        {/* Footer */}
        <div className="border-t p-4 md:p-6">
          <h2 className="text-lg font-bold md:text-xl">
            {t('welcomeToGroute')}
          </h2>
        </div>
      </div>

      {/* Right Panel - Animated Background + News Card */}
      <div className="hidden flex-1 p-4 md:flex">
        <div className="relative hidden flex-1 overflow-hidden md:flex md:items-center md:justify-center">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="absolute inset-0 h-full w-full rounded-2xl object-cover"
          >
            <source src="/animated-gradient.mp4" type="video/mp4" />
          </video>

          {/* News Card */}
          {/* <div className="relative z-10 flex max-w-[390px] flex-col gap-4 overflow-hidden">
            <div className="bg-muted aspect-video h-[344px] w-full overflow-hidden rounded-2xl">
              <img
                src="/tachograph.png"
                alt="Groute News"
                className="h-full w-full object-cover"
              />
            </div>
            <div className="bg-background w-full rounded-2xl shadow-2xl">
              <div className="space-y-4 p-6">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="text-lg font-semibold">Lorem ipsum dolore</h3>
                  <span className="bg-secondary text-secondary-foreground rounded-full px-3 py-0.5 text-xs font-medium">
                    {t('news')}
                  </span>
                  <span className="bg-muted text-muted-foreground rounded-full px-3 py-0.5 text-xs font-medium">
                    22.05.2026.
                  </span>
                </div>

                <p className="text-muted-foreground text-sm leading-relaxed">
                  Ut enim ad minim veniam, quis nostrud exercitation ullamco
                  laboris nisi ut aliquip ex ea commodo consequat. Duis aute
                  irure dolor in reprehenderit in vo...
                </p>

                <Button variant="outline" className="w-full rounded-full">
                  {t('readMore')}
                </Button>
              </div>
            </div>
          </div> */}
        </div>
      </div>
    </div>
  )
}
